/**
 * 型號的新增／編輯／覆核表單。作業設定與進庫建檔共用。
 *
 * 為什麼要共用：現場遇到沒見過的型號時，最自然的位置是「進庫建檔」那個型號下拉旁邊，
 * 而不是要他先離開流程、跑去作業設定、建完再回來——那等於還是卡住了。
 * 同一個表單放兩個入口，行為必須一模一樣，所以只寫一份。
 *
 * §7.5 的做法：驗證跑的是 `shared/validate/modelSpec.ts`——**與後端同一份程式碼**，
 * 所以打字的當下就看得到錯在哪，而不是送出去才知道。後端仍然會再驗一次（API 是公開的）。
 */

import { useState } from 'react'
import { ApiError, api } from '../api/client.js'
import { Alert, Button, Field, Mono, TextInput } from './ui.js'
import { useConfig, useDict } from '../config/ConfigContext.js'
import type { PanelModelSpec } from '../../../shared/types/domain.js'
import { validateModelSpec } from '../../../shared/validate/modelSpec.js'

export interface ModelRow extends PanelModelSpec {
  status: 'provisional' | 'confirmed'
  usage: { panels: number; workOrders: number }
  used: boolean
  usageLabel: string
  /** 已覆核或已被使用 —— 只有主管動得了。由伺服器判斷，前端不自己推導 */
  locked: boolean
  canEdit: boolean
  lockedReason: string | null
}

export interface ModelList {
  modelSpecs: ModelRow[]
  canCreate: boolean
  canConfirm: boolean
}

export type FormMode = 'create' | 'edit' | 'confirm'

/**
 * 新增／編輯／覆核共用的表單。
 *
 * §7.5 的做法：驗證跑的是 `shared/validate/modelSpec.ts`——**與後端同一份程式碼**，
 * 所以打字的當下就看得到錯在哪，而不是送出去才知道。後端仍然會再驗一次（API 是公開的）。
 */
export function ModelFormModal({ mode, row, existing, canConfirm, onClose, onDone }: {
  mode: FormMode
  row?: ModelRow
  existing: string[]
  canConfirm: boolean
  onClose: () => void
  onDone: (msg: string) => void
}) {
  const dict = useDict()
  const { config } = useConfig()
  const [modelCode, setModelCode] = useState(row?.modelCode ?? '')
  const [label, setLabel] = useState(row?.label ?? '')
  const [manufacturer, setManufacturer] = useState(row?.manufacturer ?? '')
  const [ratedW, setRatedW] = useState(row?.ratedW ? String(row.ratedW) : '')
  const [cellCount, setCellCount] = useState(row?.cellCount ? String(row.cellCount) : '')
  const [totalKg, setTotalKg] = useState(row?.totalKg ? String(row.totalKg) : '')
  const [note, setNote] = useState(row?.note ?? '')
  const [comp, setComp] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(row?.composition ?? {})) out[k] = String(v)
    return out
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const materials = (config?.materialDict ?? []).filter((m) => m.activeInMockup !== false)

  // 現場申請的是暫定型號；主管填滿基準時才會變成已覆核
  const targetStatus: 'provisional' | 'confirmed' =
    mode === 'confirm' ? 'confirmed'
      : mode === 'edit' ? (row?.status ?? 'confirmed')
        : canConfirm && totalKg.trim() !== '' && Object.keys(comp).length > 0 ? 'confirmed' : 'provisional'

  const draft = {
    modelCode: modelCode.trim(),
    label: label.trim(),
    manufacturer: manufacturer.trim() || null,
    ratedW: ratedW.trim() === '' ? null : Number(ratedW),
    cellCount: cellCount.trim() === '' ? 0 : Number(cellCount),
    totalKg: totalKg.trim() === '' ? 0 : Number(totalKg),
    composition: Object.fromEntries(
      Object.entries(comp)
        .filter(([, v]) => v.trim() !== '')
        .map(([k, v]) => [k, Number(v)]),
    ) as PanelModelSpec['composition'],
  }

  const issues = validateModelSpec(draft, {
    knownMaterialCodes: materials.map((m) => m.materialCode),
    existingModelCodes: mode === 'create' ? existing : [],
    isEdit: mode !== 'create',
    status: targetStatus,
  })
  const issueFor = (field: string) => issues.find((i) => i.field === field)?.message
  const compSum = Object.values(draft.composition).reduce((a, v) => a + (v ?? 0), 0)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const body = {
        label: draft.label,
        manufacturer: draft.manufacturer,
        ratedW: draft.ratedW,
        cellCount: draft.cellCount || null,
        totalKg: draft.totalKg || null,
        composition: Object.keys(draft.composition).length > 0 ? draft.composition : null,
        note: note.trim() || null,
      }
      if (mode === 'create') {
        const r = await api.post<{ modelSpec: PanelModelSpec; notice?: string }>(
          '/api/config/model-specs', { modelCode: draft.modelCode, ...body })
        onDone(r.notice ?? `型號 ${r.modelSpec.modelCode} 已建立。`)
      } else if (mode === 'confirm') {
        const r = await api.post<{ notice: string }>(
          `/api/config/model-specs/${encodeURIComponent(row!.modelCode)}/confirm`, body)
        onDone(r.notice)
      } else {
        await api.patch(`/api/config/model-specs/${encodeURIComponent(row!.modelCode)}`, body)
        onDone(`型號 ${row!.modelCode} 已更新。`)
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  const title = mode === 'create' ? '新增型號' : mode === 'confirm' ? '補基準並覆核' : `編輯 ${row?.modelCode}`

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4">
      <div className="w-full max-w-xl rounded-lg border border-ink-200 bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="font-semibold text-ink-900">{title}</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700" aria-label="關閉">✕</button>
        </div>

        {mode === 'create' && !canConfirm && (
          <Alert tone="info" title="你建立的會是「暫定型號」">
            <p>
              只要填<strong>型號代碼</strong>與<strong>名稱</strong>就能建立，馬上就可以選用，
              流程不會卡住。組成比例不知道就<strong>留空</strong>——
              暫定型號不參與任何數字計算，跟「未辨識」完全一樣，所以留空不會弄髒帳。
            </p>
            <p className="mt-1">
              <strong>不要猜數字填進去</strong>。猜錯的基準會被拿去算後續每一片板子的分攤，
              而且帳面上看不出來。主管會補齊並覆核。
            </p>
          </Alert>
        )}

        {mode === 'confirm' && (
          <Alert tone="warn" title="覆核之後基準就開始生效">
            <p>
              <Mono>{row?.modelCode}</Mono> 由 <strong>{row?.createdBy ?? '—'}</strong> 申請
              {row?.note ? `，說明：${row.note}` : ''}。目前已被 {row?.usageLabel}。
            </p>
            <p className="mt-1">
              覆核後，掛這個型號的板子會<strong>從現在起</strong>改用這份基準計算分攤；
              先前已經過帳的數字不受影響（事件流保留當時的基準）。
            </p>
          </Alert>
        )}

        {mode === 'edit' && row?.locked && (
          <Alert
            tone="warn"
            title={
              row.status === 'confirmed' && row.used
                ? `這個型號已經覆核，而且已被 ${row.usageLabel}用過`
                : row.status === 'confirmed'
                  ? `這個型號已經由 ${row.confirmedBy ?? '主管'} 覆核`
                  : `這個型號已經被 ${row.usageLabel}用過`
            }
          >
            {row.used
              ? <>改動基準會影響它們<strong>後續</strong>的分攤數字。歷史數字不受影響。</>
              : <>覆核過的基準是全公司的權威值，<strong>下一片</strong>掛上這個型號的板子就會用它計算。</>}
          </Alert>
        )}

        <div className="mt-3 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="型號代碼" hint={mode === 'create' ? '英數字、減號、底線，2–24 字。建立後不可修改' : '不可修改'} required>
              <TextInput
                value={modelCode}
                autoFocus={mode === 'create'}
                disabled={mode !== 'create'}
                className="font-mono"
                placeholder="JKM-410M"
                onChange={(e) => setModelCode(e.target.value)}
              />
              {issueFor('modelCode') && <p className="mt-1 text-xs text-red-700">{issueFor('modelCode')}</p>}
            </Field>
            <Field label="名稱" hint="畫面上顯示的文字" required>
              <TextInput value={label} placeholder="晶科 410W 單晶"
                onChange={(e) => setLabel(e.target.value)} />
              {issueFor('label') && <p className="mt-1 text-xs text-red-700">{issueFor('label')}</p>}
            </Field>
          </div>

          {/*
            設計書 §5.1 的面板主檔要求「廠牌型號、額定功率」。先前沒有這兩個欄位，
            現場只能把品牌塞進名稱裡（「晶科 410W 單晶」），於是搜尋與彙總都用不上。
            兩個都選填——現場看得到標籤就填，看不清楚就留空，不要卡住流程。
          */}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="品牌／製造商" hint="標籤上的廠牌，例如「晶科能源 JinkoSolar」">
              <TextInput value={manufacturer} placeholder="看不到就留空"
                onChange={(e) => setManufacturer(e.target.value)} />
              {issueFor('manufacturer') && <p className="mt-1 text-xs text-red-700">{issueFor('manufacturer')}</p>}
            </Field>
            <Field label="額定功率（W）" hint="現場講的「410 瓦那款」。不參與重量計算">
              <TextInput inputMode="numeric" value={ratedW} placeholder="410"
                onChange={(e) => setRatedW(e.target.value.replace(/[^\d]/g, ''))} />
              {issueFor('ratedW') && <p className="mt-1 text-xs text-red-700">{issueFor('ratedW')}</p>}
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="電池片數" hint={targetStatus === 'provisional' ? '不知道就留空' : '覆核必填'}
              required={targetStatus === 'confirmed'}>
              <TextInput inputMode="numeric" value={cellCount}
                onChange={(e) => setCellCount(e.target.value.replace(/[^\d]/g, ''))} />
              {issueFor('cellCount') && <p className="mt-1 text-xs text-red-700">{issueFor('cellCount')}</p>}
            </Field>
            <Field label="單片總重（kg）" hint={targetStatus === 'provisional' ? '不知道就留空' : '覆核必填'}
              required={targetStatus === 'confirmed'}>
              <TextInput inputMode="decimal" value={totalKg}
                onChange={(e) => setTotalKg(e.target.value.replace(/[^\d.]/g, ''))} />
              {issueFor('totalKg') && <p className="mt-1 text-xs text-red-700">{issueFor('totalKg')}</p>}
            </Field>
          </div>

          <div>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-xs font-medium text-ink-600">
                組成比例
                {targetStatus === 'confirmed' && <span className="ml-0.5 text-red-600">*</span>}
              </span>
              <span className={`text-xs tabular-nums ${
                Object.keys(draft.composition).length === 0 ? 'text-ink-400'
                  : Math.abs(compSum - 1) <= 0.005 ? 'text-emerald-700' : 'text-amber-700'}`}>
                加總 {(compSum * 100).toFixed(1)}%
                {Object.keys(draft.composition).length > 0 && Math.abs(compSum - 1) <= 0.005 && ' ✓'}
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {materials.map((m) => (
                <div key={m.materialCode} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 text-xs text-ink-600">{m.label}</span>
                  <TextInput
                    inputMode="decimal"
                    value={comp[m.materialCode] ?? ''}
                    placeholder="0.000"
                    className="font-mono text-sm"
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d.]/g, '')
                      setComp((c) => {
                        const next = { ...c }
                        if (v === '') delete next[m.materialCode]
                        else next[m.materialCode] = v
                        return next
                      })
                    }}
                  />
                </div>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-ink-400">
              相對整片總重的比例，<Mono>0.722</Mono> 表示 72.2%。加總必須是 100%。
              {targetStatus === 'provisional' && ' 暫定型號可以全部留空。'}
            </p>
            {issueFor('composition') && <p className="mt-1 text-xs text-red-700">{issueFor('composition')}</p>}
            {issues.filter((i) => i.field.startsWith('composition.')).map((i) => (
              <p key={i.field} className="mt-1 text-xs text-red-700">{dict.material(i.field.split('.')[1] ?? '')}：{i.message}</p>
            ))}
          </div>

          <Field label="說明" hint="例如「背面標籤寫 JKM-410M，組成未知」。主管覆核時看得到">
            <TextInput value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>

        {targetStatus === 'provisional' && mode !== 'edit' && (
          <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            這筆會存成 <strong>暫定型號</strong>，等主管補齊基準後覆核。在那之前它不參與任何數字計算。
          </p>
        )}

        {error && <div className="mt-3"><Alert tone="danger">{error}</Alert></div>}

        <div className="mt-4 flex gap-2">
          <Button variant="primary" className="flex-1" disabled={busy || issues.length > 0}
            onClick={() => void submit()}>
            {busy ? '請稍候…'
              : mode === 'create' ? (targetStatus === 'provisional' ? '建立暫定型號' : '建立型號')
                : mode === 'confirm' ? '我確認基準無誤，覆核' : '儲存'}
          </Button>
          <Button onClick={onClose} disabled={busy}>取消</Button>
        </div>
      </div>
    </div>
  )
}
